import { Box, FormControl, Grid, InputLabel, MenuItem, Select, Typography } from '@mui/material'
import MDAccordion from 'components/MDAccordian'
import MDButton from 'components/MDButton'
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import ParameterForm from './parameterForm'
import RequiredFieldForm from './requiredFieldForm'
import ProcessingRulesForm from './processingRulesForm'
import { useMediaQuery } from 'react-responsive'
import CrudService from 'services/cruds-service'
import FilterForm from './filterForm'

const BATCH_SIZE = 5;

function JobForm({ selectedCompanyRows, filterData, updateStatus, updateDashboardData, fetchData, type, children }) {
    const isTabletOrMobile = useMediaQuery({ query: '(max-width: 1224px)' })
    const [data, setData] = useState([])
    const [jobTypeId, setJobTypeId] = useState('')  // Initialize with empty string instead of undefined
    const [jobType, setJobType] = useState(null)    // Initialize with null instead of undefined
    const [jobStatus, setJobStatus] = useState(false)
    const [endpoint, setEndpoint] = useState("")
    const [parameterState, setParameterState] = useState({})  // Initialize with empty object
    const [requiredState, setRequiredState] = useState([])    // Initialize with empty array
    const [processingState, setProcessingState] = useState({}) // Initialize with empty object
    const [filterState, setFilterState] = useState({}) // Initialize with empty object
    const isRunning = useRef(false)

    const resetStatusToIdle = useCallback(() => {
        filterData.forEach((_, index) => {
            const statuses = ["validation", "fetch", "content", "update"]
            statuses.forEach(status => {
                updateStatus(status, "idle", `${status} idle`, index)
            })
        })
    }, [filterData, updateStatus])

    useEffect(() => {
        const fetchJobTypes = async () => {
            try {
                const response = await CrudService.getJobTypes()
                setData(response.data)
            } catch (err) {
                console.error('Error fetching job types:', err)
            }
            resetStatusToIdle()
        }
        fetchJobTypes()
    }, [])

    const initialState = (data) => {
        return Object.fromEntries(
          Object.entries(data).map(([key, value]) => [
            key,
            Array.isArray(value.options) ? value.default : value,
          ])
        );
      };

    const selectJobType = useCallback((value) => {
        const selectedJob = data.find(e => e.job_type_id === value)
        setJobTypeId(value)
        setJobType(selectedJob)
        setEndpoint(selectedJob.destination_url)
        setParameterState({})
        setRequiredState([])
        setProcessingState({})
        setFilterState({})
        resetStatusToIdle()
    }, [data])

    const handleValidation = async (company, elmIndex) => {
        const validationRes = await CrudService.checkValidation({
            company_id: company,
            processingState,
            requiredState: requiredState.missing_value_action.fail,
            validations: jobType?.validation
        })

        if(validationRes.shouldContinue) {
            updateStatus('validation', 'ignore', 'No Need to call API', elmIndex)
            return false
        }

        if(!validationRes.requiredValidation) {
            updateStatus('validation', 'idle', 'validation not required', elmIndex)
            return true
        }

        if(validationRes.validationValid) {
            updateStatus('validation', 'valid', 'validation valid', elmIndex)
            return true
        }

        updateStatus('validation', 'invalid', validationRes.validationMsg, elmIndex)
        return false
    }

    const handleFilter = async (company, elmIndex) => {
        const updatedFilters = filterState ? Object.keys(filterState).reduce((acc, key) => {
            acc[key] = {
              ...filterState[key],
              filters: filterState[key].filters.map((filter) => {
                if (filter.condition === "LIKE") {
                  return {  
                    ...filter,
                    value: `%${filter.value}%` 
                  };
                }
                return filter;
              })
            };
            return acc;
          }, {}) : filterState;

        const validationRes = await CrudService.checkFilter({
            campaign: company,
            filters: updatedFilters
        })

        if(!validationRes.success) {
            updateStatus('validation', 'ignore', validationRes.message, elmIndex)
            return false
        }

        if(validationRes.success) {
            updateStatus('validation', 'valid', 'Filter Valid', elmIndex)
            return true
        }

        updateStatus('validation', 'invalid', validationRes.error, elmIndex)
        return false
    }

    const processCompany = async (company, updatedFilterData) => {
        const elmIndex = updatedFilterData.findIndex(c => c.company_id === company.company_id)

        if(!(await handleFilter(company.uuid, elmIndex))) {
            return
        }

        if(!(await handleValidation(company.company_id, elmIndex))) {
            return
        }

        updateStatus('fetch', 'running', 'fetch started', elmIndex)

        try {
            const fetchResponse = await CrudService.fetchResponseFromOpenAI({
                job_type_id: jobTypeId,
                payload: parameterState ? { ...initialState(parameterState) } : {},
                processing_rules: processingState,
                company_id: company.company_id,
                campaign_id: company.campaign_id
            })

            updateStatus('content', 'success', { 
                content: fetchResponse?.content, 
                sanitizeData: fetchResponse?.sanitizeData 
            }, elmIndex)

            if (fetchResponse?.sanitizeData && 
                !Object.values(fetchResponse.sanitizeData).length && 
                jobType.response_handling === 'record') {
                updateStatus('fetch', 'success', 'sanitize data is empty, no need for update', elmIndex)
                return
            }

            updateStatus('fetch', fetchResponse.status, `fetch ${fetchResponse.status}`, elmIndex)

            if (['consume_only', 'self'].includes(jobType.response_handling)) {
                updateStatus('update', 'idle', `update ${jobType.response_handling}`, elmIndex)
                if(type !== 'dashboard'){
                    const data = await CrudService.getCampaignCompanyWithExternal(company.company_id)
                    updatedFilterData[elmIndex].CampaignCompany = data.data
                }
                return
            }
            // if(type !== 'dashboard'){
                await handleUpdate(company, fetchResponse, updatedFilterData, elmIndex)
            // }

        } catch (err) {
            console.error('Processing error:', err)
            updateStatus('fetch', err.status, err.msg || 'Unknown error', elmIndex)
        }
    }

    const handleUpdate = async (company, fetchResponse, updatedFilterData, elmIndex) => {
        updateStatus('update', 'running', 'update started', elmIndex)

        try {
            const data = await CrudService.updateCompanies({
                company_id: company.company_id,
                data: { ...fetchResponse.sanitizeData, campaign_id: company.campaign_id },
                processing_rules: processingState,
                type
            })

            updateStatus('update', data.status, `update ${data.status}`, elmIndex)
            if(type !== 'dashboard'){
                updatedFilterData[elmIndex].CampaignCompany = data?.updatedRecords
                updateDashboardData(data.updatedRecords, elmIndex)
            }
        } catch (err) {
            console.error('Update error:', err)
            updateStatus('update', err.status, err.msg || 'Unknown error', elmIndex)
        }
    }

    const runJob = async () => {
        if (!selectedCompanyRows.length) {
            alert('Please select records')
            return
        }

        if (jobType.response_handling === 'ignore') {
            alert('Response handling is ignore, not able to run this job')
            return
        }

        resetStatusToIdle()
        setJobStatus(true)
        isRunning.current = true

        const filteredCompanies = filterData.filter(company => 
            selectedCompanyRows.includes(company.company_id)
        )
        const updatedFilterData = [...filterData]

        const chunks = Array(Math.ceil(filteredCompanies.length / BATCH_SIZE))
            .fill()
            .map((_, i) => filteredCompanies.slice(i * BATCH_SIZE, (i + 1) * BATCH_SIZE))

        for (const chunk of chunks) {
            if (!isRunning.current) {
                break
            }

            await Promise.all(chunk.map(company => 
                processCompany(company, updatedFilterData)
            ))
        }
        if(type === 'dashboard'){
            fetchData()
        }

        setJobStatus(false)
    }

    const stopJob = useCallback(() => {
        isRunning.current = false
        setJobStatus(false)
    }, [])

    const renderJobTypeSelect = useMemo(() => (
        <Grid item xs={isTabletOrMobile ? 8 : 4}>
            <FormControl fullWidth>
                <InputLabel>Job Type</InputLabel>
                <Select
                    value={jobTypeId || ''}  // Ensure value is never undefined
                    onChange={(e) => selectJobType(e.target.value)}
                    label="Job Type"
                    fullWidth
                    sx={{ height: '45px', '& .MuiSelect-select': { height: '45px !important' } }}
                >
                    {data.map((f, idx) => (
                        <MenuItem key={idx} value={f.job_type_id}>
                            {f.job_name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Grid>
    ), [data, jobTypeId, isTabletOrMobile, selectJobType])

    const renderActionButtons = useMemo(() => (
        jobTypeId && (
            <Grid item xs={4}>
                <Box sx={{ display: "flex", gap: 2 }}>
                    {!jobStatus ? (
                        <MDButton variant="gradient" color="info" onClick={runJob}>
                            Run
                        </MDButton>
                    ) : (
                        <MDButton variant="gradient" color="info" onClick={stopJob}>
                            Stop
                        </MDButton>
                    )}
                </Box>
            </Grid>
        )
    ), [jobTypeId, jobStatus, runJob, stopJob])

    return (
        <Box sx={{ pt: 1, pb: 1, width: '100%' }}>
            <Grid container spacing={3} alignItems="center">
                {children}
                {renderJobTypeSelect}
                {renderActionButtons}
            </Grid>

            {endpoint && (
                <Typography variant='h6' sx={{ fontWeight: '100', mb: 1 }}>
                    Endpoint: {endpoint}
                </Typography>
            )}

            {(jobType?.parameters || jobType?.validations || jobType?.processing_rules) && (
                <MDAccordion summary={'Job Validation & Required Fields'}>
                    <Grid container spacing={3}>
                        {jobType?.parameters && (
                            <ParameterForm 
                                data={jobType.parameters} 
                                setParameterState={setParameterState} 
                            />
                        )}
                        {jobType?.validations && (
                            <RequiredFieldForm 
                                data={jobType.validations} 
                                setRequiredState={setRequiredState} 
                            />
                        )}
                    </Grid>
                    {jobType?.processing_rules && (
                        <ProcessingRulesForm 
                            data={jobType.processing_rules} 
                            setProcessingState={setProcessingState} 
                        />
                    )}
                    <FilterForm
                        data={jobType.filters}
                        setFilterState={setFilterState}
                    />
                </MDAccordion>
            )}
        </Box>
    )
}

export default React.memo(JobForm)